import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useTenantStore } from '@/stores/tenantStore';
import { showApiError, invokeWithErrorHandling, ERROR_CONTEXTS } from '@/lib/error-handler';
import { ERROR_TAXONOMY, type ErrResp } from '@/lib/errors';

interface DemoSession {
  demo_token: string;
  restaurant_id: string;
  expires_at: string;
  subdomain: string;
}

interface DemoContextSuccess {
  ok: true;
  data: {
    restaurant_id: string;
    name: string;
    subdomain: string;
    expires_at: string;
  };
}

interface DemoContextFailure {
  ok: false;
  error?: ErrResp["error"];
}

type DemoContextResponse = DemoContextSuccess | DemoContextFailure;

interface DemoCreateResponse {
  demo_token: string;
  restaurant_id: string;
  subdomain: string;
  expires_at: string;
}

interface DemoConvertResponse {
  ok: true;
  data: { restaurant_id: string };
}

interface DemoConvertAuthFailure {
  ok: false;
  error?: { needs_auth?: boolean };
}

type DemoConvertResult = DemoConvertResponse | DemoConvertAuthFailure;

type DemoActionResult =
  | { success: true; restaurant_id?: string }
  | { success: false; error: string; needs_auth?: boolean };

const isDemoContextSuccess = (payload: DemoContextResponse | null): payload is DemoContextSuccess =>
  payload?.ok === true;

const isDemoConvertSuccess = (payload: DemoConvertResult | null): payload is DemoConvertResponse =>
  payload?.ok === true;

const DEMO_TOKEN_KEY = 'braise:demo_token';
const LEGACY_DEMO_TOKEN_KEY = 'eataz:demo_token';
const DEMO_RESTAURANT_KEY = 'braise:restaurant_id';
const LEGACY_DEMO_RESTAURANT_KEY = 'eataz:restaurant_id';
const DEMO_SUBDOMAIN_KEY = 'braise:subdomain';
const LEGACY_DEMO_SUBDOMAIN_KEY = 'eataz:subdomain';

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return typeof window.localStorage !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const toError = (value: unknown, fallback: string): Error => {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string') {
    return new Error(value);
  }
  if (value && typeof value === 'object') {
    try {
      return new Error(JSON.stringify(value));
    } catch {
      return new Error(fallback);
    }
  }
  return new Error(fallback);
};

const readWithLegacyMigration = (
  store: Storage | null,
  primaryKey: string,
  legacyKey: string,
): string | null => {
  if (!store) {
    return null;
  }

  try {
    const primary = store.getItem(primaryKey);
    if (typeof primary === 'string' && primary.length > 0) {
      return primary;
    }

    const legacy = store.getItem(legacyKey);
    if (typeof legacy === 'string' && legacy.length > 0) {
      store.setItem(primaryKey, legacy);
      return legacy;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug('[useDemo] failed to migrate legacy storage key', { primaryKey, legacyKey, error });
    }
  }

  return null;
};

interface UseDemoResult {
  isDemo: boolean;
  demoSession: DemoSession | null;
  timeLeft: number;
  timeLeftFormatted: string;
  isLoading: boolean;
  startDemo: (name: string, subdomain: string) => Promise<DemoActionResult>;
  convertToReal: () => Promise<DemoActionResult>;
  endDemo: () => Promise<void>;
}

export const useDemo = (): UseDemoResult => {
  const [demoSession, setDemoSession] = useState<DemoSession | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { setActiveTenant, clearTenant } = useTenantStore();
  const storage = getStorage();

  const clearDemoSession = useCallback((): void => {
    storage?.removeItem(DEMO_TOKEN_KEY);
    storage?.removeItem(DEMO_RESTAURANT_KEY);
    storage?.removeItem(DEMO_SUBDOMAIN_KEY);
    storage?.removeItem('needsOnboarding');
    setDemoSession(null);
    setIsDemo(false);
    setTimeLeft(0);
    clearTenant();
  }, [clearTenant, storage]);

  const validateDemoSession = useCallback(
    async (demoToken: string): Promise<void> => {
      try {
        const response = await supabase.functions.invoke<DemoContextResponse>('demo-get-context', {
          body: { demo_token: demoToken },
        });

        if (response.error) {
          throw toError(response.error, 'Failed to fetch demo context');
        }

        if (isDemoContextSuccess(response.data)) {
          const { restaurant_id, name, subdomain, expires_at } = response.data.data;
          const session: DemoSession = {
            demo_token: demoToken,
            restaurant_id,
            expires_at,
            subdomain,
          };

          setDemoSession(session);
          setIsDemo(true);
          setTimeLeft(Math.floor((new Date(expires_at).getTime() - Date.now()) / 1000));
          setActiveTenant(restaurant_id, subdomain, name, true);
        } else {
          clearDemoSession();
        }
      } catch (error) {
        console.error('Error validating demo session:', error);
        clearDemoSession();
      }
    },
    [clearDemoSession, setActiveTenant],
  );

  // Check for existing demo session on mount
  useEffect(() => {
    if (!storage) {
      return;
    }

    const demoToken = readWithLegacyMigration(storage, DEMO_TOKEN_KEY, LEGACY_DEMO_TOKEN_KEY);
    const restaurantId = readWithLegacyMigration(
      storage,
      DEMO_RESTAURANT_KEY,
      LEGACY_DEMO_RESTAURANT_KEY,
    );
    const subdomain = readWithLegacyMigration(storage, DEMO_SUBDOMAIN_KEY, LEGACY_DEMO_SUBDOMAIN_KEY);

    if (demoToken && restaurantId && subdomain) {
      validateDemoSession(demoToken).catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('[useDemo] validateDemoSession rejected during mount', error);
        }
      });
    }
  }, [storage, validateDemoSession]);

  // Update time left every second when in demo
  useEffect(() => {
    if (!demoSession) {
      return;
    }

    const updateTimeLeft = () => {
      const expiresAt = new Date(demoSession.expires_at);
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();
      
      if (diff <= 0) {
        clearDemoSession();
        toast({
          title: 'Demo Expired',
          description: 'Your demo session has expired.',
        });
        // Redirect to demo expired page
        window.location.href = '/demo-expired';
      } else {
        setTimeLeft(Math.floor(diff / 1000));
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [demoSession, toast, clearDemoSession]);

  const startDemo = useCallback(async (name: string, subdomain: string): Promise<DemoActionResult> => {
    setIsLoading(true);
    try {
      const { data, error } = await invokeWithErrorHandling<DemoCreateResponse>(
        supabase,
        'demo-create',
        { body: { name, subdomain } },
        'demo'
      );

      if (error) {
        showApiError(error, ERROR_CONTEXTS.demo);
        return { success: false, error: error.error.message };
      }

      if (data) {
        const { demo_token, restaurant_id, subdomain: createdSubdomain, expires_at } = data;
        
        const session: DemoSession = {
          demo_token,
          restaurant_id,
          expires_at,
          subdomain: createdSubdomain
        };

        setDemoSession(session);
        setIsDemo(true);

        storage?.setItem(DEMO_TOKEN_KEY, demo_token);
        storage?.setItem(DEMO_RESTAURANT_KEY, restaurant_id);
        storage?.setItem(DEMO_SUBDOMAIN_KEY, createdSubdomain);

        setTimeLeft(Math.floor((new Date(expires_at).getTime() - Date.now()) / 1000));

        // Set tenant context
        setActiveTenant(restaurant_id, createdSubdomain, name, true);

        console.log('Demo created successfully:', { session });

        toast({
          title: "Demo Started!",
          description: `Your demo restaurant "${name}" is now active for 24 hours!`,
        });

        return { success: true, restaurant_id };
      }
      
      return { success: false, error: 'No data returned' };
    } catch (error: unknown) {
      console.error('Error starting demo:', error);
      showApiError(null, ERROR_CONTEXTS.demo);
      const message = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [toast, setActiveTenant, storage]);

  const convertToReal = useCallback(async (): Promise<DemoActionResult> => {
    if (!demoSession) {
      return { success: false, error: 'No active demo session' };
    }
    
    setIsLoading(true);
    try {
      const response = await supabase.functions.invoke<DemoConvertResult>('demo-convert-to-real', {
        body: { demo_token: demoSession.demo_token },
      });

      if (response.error) {
        throw toError(response.error, 'Failed to convert demo session');
      }

      if (isDemoConvertSuccess(response.data)) {
        // Clear demo state
        clearDemoSession();
        
        toast({
          title: "Your restaurant is live! 🎉",
          description: "Demo converted successfully. You can now invite staff and accept payments.",
        });

        return { success: true, restaurant_id: response.data.data.restaurant_id };
      }

      if (response.data && !response.data.ok && response.data.error?.needs_auth) {
        return { success: false, error: 'Authentication required', needs_auth: true };
      }

      showApiError({ ok: false, error: ERROR_TAXONOMY.DEMO.CONVERT_FAILED, request_id: '' }, ERROR_TAXONOMY.DEMO.CONVERT_FAILED);
      return { success: false, error: 'Conversion failed' };
    } catch (error: unknown) {
      console.error('Error converting demo:', error);
      showApiError(null, ERROR_TAXONOMY.DEMO.CONVERT_FAILED);
      const message = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [demoSession, toast, clearDemoSession]);

  const endDemo = useCallback(async (): Promise<void> => {
    if (!demoSession) {
      return;
    }
    
    setIsLoading(true);
    try {
      // Call backend to clean up demo data
      const response = await supabase.functions.invoke<{ ok?: boolean }>('demo-end', {
        body: { demo_key: demoSession.demo_token }
      });

      if (response.error) {
        if (import.meta.env.DEV) {
          console.error('Backend cleanup failed:', response.error);
        }
        toast({
          title: "Warning",
          description: "Demo ended locally but backend cleanup may have failed.",
          variant: "destructive",
        });
      } else if (response.data?.ok) {
        toast({
          title: "Demo Ended",
          description: "Your demo session and all data have been cleaned up.",
        });
      } else {
        console.error('Backend cleanup returned unexpected payload:', response.data);
        toast({
          title: "Warning", 
          description: "Demo ended locally but backend cleanup may have failed.",
          variant: "destructive",
        });
      }

      // Always clear local state regardless of backend result
      clearDemoSession();

      // Redirect to home
      window.location.href = '/';
    } catch (error: unknown) {
      console.error('Error ending demo:', error);
      toast({
        title: "Error",
        description: "Failed to end demo properly. Clearing local data.",
        variant: "destructive",
      });

      // Clear local state even if there was an error
      clearDemoSession();
      window.location.href = '/';
    } finally {
      setIsLoading(false);
    }
  }, [demoSession, toast, clearDemoSession]);

  const formatTimeLeft = useCallback((seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }, []);

  return {
    isDemo,
    demoSession,
    timeLeft,
    timeLeftFormatted: formatTimeLeft(timeLeft),
    isLoading,
    startDemo,
    convertToReal,
    endDemo,
  };
};
